import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  LinearProgress,
  Paper,
  Radio,
  RadioGroup,
  Slider,
  Stack,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ArrowBackRounded, ArrowForwardRounded, CheckCircleRounded, CloseRounded, DescriptionOutlined } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import api, { questionnairesAPI } from '../services/api';

const glassSurface = (theme) => ({
  background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.88)} 0%, ${alpha(theme.palette.background.paper, 0.72)} 100%)`,
  backdropFilter: 'blur(24px) saturate(165%)',
  WebkitBackdropFilter: 'blur(24px) saturate(165%)',
  border: `1px solid ${alpha(theme.palette.common.white, 0.72)}`,
  boxShadow: `0 18px 55px ${alpha(theme.palette.common.black, 0.08)}, inset 0 1px 0 ${alpha(theme.palette.common.white, 0.9)}`,
});

const getQuestionOptions = (question) => {
  if (!Array.isArray(question?.options)) return [];
  return question.options.map((option) => {
    if (typeof option === 'string' || typeof option === 'number') return String(option);
    return option?.label ?? option?.text ?? option?.value ?? '';
  }).filter(Boolean);
};

const QuestionnairePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const primary = theme.palette.primary.main;

  const [questionnaire, setQuestionnaire] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [answers, setAnswers] = useState({});
  const [activeStep, setActiveStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [consent, setConsent] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const loadQuestionnaire = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (!id || id === 'null' || !/^\d+$/.test(id)) {
        throw new Error('Некорректный идентификатор анкеты.');
      }
      const response = await api.get(`/questionnaires/${id}/`);
      const data = response?.data;
      if (!data || String(data.id) !== String(id) || !Array.isArray(data.questions)) {
        throw new Error(`Не удалось получить вопросы анкеты с ID ${id}`);
      }

      setQuestionnaire(data);
      const initialAnswers = {};
      data.questions.forEach((question) => {
        if (question.question_type === 'multiple_choice') initialAnswers[question.id] = [];
        else if (question.question_type === 'scale') initialAnswers[question.id] = 5;
        else initialAnswers[question.id] = '';
      });
      setAnswers(initialAnswers);
      setActiveStep(0);
      setConsent(false);
      setValidationMessage('');
      setSubmitError('');
      setSubmitted(false);
    } catch (err) {
      console.error('Ошибка загрузки анкеты:', err);
      setError(err?.response?.data?.detail || err.message || 'Не удалось загрузить анкету. Проверьте, что она существует и доступна вам.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadQuestionnaire();
  }, [loadQuestionnaire]);

  const questions = questionnaire?.questions ?? [];
  const currentQuestion = questions[activeStep];
  const progress = questions.length ? ((activeStep + 1) / questions.length) * 100 : 0;
  const isLastStep = activeStep === questions.length - 1;

  const handleAnswerChange = (questionId, value) => {
    setAnswers((previous) => ({ ...previous, [questionId]: value }));
    setValidationMessage('');
  };

  const handleMultipleChoiceChange = (questionId, option, checked) => {
    setAnswers((previous) => {
      const selected = previous[questionId] || [];
      return {
        ...previous,
        [questionId]: checked
          ? (selected.includes(option) ? selected : [...selected, option])
          : selected.filter((item) => item !== option),
      };
    });
    setValidationMessage('');
  };

  const questionIsAnswered = (question) => {
    if (!question?.is_required) return true;
    const answer = answers[question.id];
    if (question.question_type === 'multiple_choice') return Array.isArray(answer) && answer.length > 0;
    if (question.question_type === 'scale') return Number.isFinite(Number(answer));
    return answer !== undefined && answer !== null && String(answer).trim() !== '';
  };

  const handleNext = () => {
    if (!questionIsAnswered(currentQuestion)) {
      setValidationMessage('Пожалуйста, ответьте на обязательный вопрос, чтобы продолжить.');
      return;
    }
    setValidationMessage('');
    setActiveStep((step) => Math.min(step + 1, questions.length - 1));
    window.scrollTo?.({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    setValidationMessage('');
    setActiveStep((step) => Math.max(step - 1, 0));
    window.scrollTo?.({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    if (!questionIsAnswered(currentQuestion)) {
      setValidationMessage('Пожалуйста, ответьте на обязательный вопрос, чтобы завершить анкету.');
      return;
    }
    if (!consent) {
      setValidationMessage('Подтвердите согласие на обработку ответов, чтобы отправить анкету.');
      return;
    }

    setSubmitting(true);
    setSubmitError('');
    setValidationMessage('');
    try {
      const formattedAnswers = Object.entries(answers).map(([questionId, answerData]) => ({
        question_id: Number.parseInt(questionId, 10),
        answer_data: answerData,
      }));
      await questionnairesAPI.submitQuestionnaire(id, formattedAnswers);
      setSubmitted(true);
      window.scrollTo?.({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Ошибка отправки анкеты:', err);
      setSubmitError(err?.response?.data?.detail || err.message || 'Не удалось отправить анкету. Попробуйте ещё раз.');
    } finally {
      setSubmitting(false);
    }
  };

  const optionSx = useMemo(() => ({
    m: 0,
    px: 1.5,
    py: 0.65,
    width: '100%',
    borderRadius: 2.5,
    border: `1px solid ${alpha(theme.palette.divider, 0.72)}`,
    backgroundColor: alpha(theme.palette.background.paper, 0.55),
    transition: 'border-color 180ms ease, background-color 180ms ease, transform 180ms ease',
    '&:hover': {
      borderColor: alpha(primary, 0.5),
      backgroundColor: alpha(primary, 0.045),
    },
    '& .MuiFormControlLabel-label': { flex: 1, fontSize: '0.97rem' },
    '& .MuiRadio-root, & .MuiCheckbox-root': { color: alpha(primary, 0.58) },
    '& .Mui-checked': { color: primary },
  }), [theme, primary]);

  const renderQuestion = (question) => {
    const currentAnswer = answers[question.id];
    const options = getQuestionOptions(question);

    switch (question.question_type) {
      case 'text':
        return (
          <TextField
            fullWidth
            multiline
            minRows={4}
            maxRows={10}
            value={currentAnswer ?? ''}
            onChange={(event) => handleAnswerChange(question.id, event.target.value)}
            placeholder="Напишите ваш ответ…"
            variant="outlined"
            sx={{
              mt: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                backgroundColor: alpha(theme.palette.background.paper, 0.65),
                '& fieldset': { borderColor: alpha(theme.palette.divider, 0.8) },
                '&:hover fieldset': { borderColor: alpha(primary, 0.6) },
                '&.Mui-focused fieldset': { borderColor: primary },
              },
            }}
          />
        );

      case 'single_choice':
        return (
          <FormControl component="fieldset" fullWidth sx={{ mt: 1 }}>
            <RadioGroup value={currentAnswer ?? ''} onChange={(event) => handleAnswerChange(question.id, event.target.value)}>
              <Stack spacing={1}>
                {options.map((option) => (
                  <FormControlLabel key={option} value={option} control={<Radio />} label={option} sx={optionSx} />
                ))}
              </Stack>
            </RadioGroup>
            {options.length === 0 && <Typography color="text.secondary" variant="body2">Для этого вопроса пока не добавлены варианты ответа.</Typography>}
          </FormControl>
        );

      case 'multiple_choice':
        return (
          <FormControl component="fieldset" fullWidth sx={{ mt: 1 }}>
            <FormGroup>
              <Stack spacing={1}>
                {options.map((option) => (
                  <FormControlLabel
                    key={option}
                    control={(
                      <Checkbox
                        checked={(currentAnswer || []).includes(option)}
                        onChange={(event) => handleMultipleChoiceChange(question.id, option, event.target.checked)}
                      />
                    )}
                    label={option}
                    sx={optionSx}
                  />
                ))}
              </Stack>
            </FormGroup>
            {options.length === 0 && <Typography color="text.secondary" variant="body2">Для этого вопроса пока не добавлены варианты ответа.</Typography>}
          </FormControl>
        );

      case 'scale':
        return (
          <Box sx={{ mt: 3, px: { xs: 1, sm: 2 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Совсем не согласен</Typography>
              <Paper elevation={0} sx={{ px: 2, py: 0.7, borderRadius: 99, color: primary, bgcolor: alpha(primary, 0.1), fontWeight: 700 }}>
                {currentAnswer ?? 5} из 5
              </Paper>
              <Typography variant="body2" color="text.secondary">Полностью согласен</Typography>
            </Box>
            <Slider
              value={Number(currentAnswer ?? 5)}
              onChange={(_, value) => handleAnswerChange(question.id, value)}
              min={1}
              max={5}
              step={1}
              marks={[1, 2, 3, 4, 5].map((value) => ({ value, label: String(value) }))}
              valueLabelDisplay="auto"
              sx={{
                color: primary,
                '& .MuiSlider-thumb': { width: 20, height: 20, boxShadow: `0 0 0 6px ${alpha(primary, 0.12)}` },
                '& .MuiSlider-rail': { opacity: 0.22 },
                '& .MuiSlider-markLabel': { color: theme.palette.text.secondary, fontSize: '0.8rem' },
              }}
            />
          </Box>
        );

      default:
        return <Alert severity="warning">Этот тип вопроса пока не поддерживается.</Alert>;
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '70vh', display: 'grid', placeItems: 'center', p: 3 }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress sx={{ color: primary }} />
          <Typography color="text.secondary">Загружаем анкету…</Typography>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: { xs: 4, md: 8 } }}>
        <Paper sx={{ ...glassSurface(theme), p: { xs: 3, sm: 4 }, borderRadius: 5 }}>
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <Button variant="contained" onClick={loadQuestionnaire} sx={{ bgcolor: primary, borderRadius: 99, textTransform: 'none' }}>Попробовать ещё раз</Button>
            <Button onClick={() => navigate('/dashboard')} sx={{ borderRadius: 99, textTransform: 'none' }}>Вернуться на главную</Button>
          </Stack>
        </Paper>
      </Container>
    );
  }

  if (!questionnaire || questions.length === 0) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Alert severity="info">В этой анкете пока нет вопросов.</Alert>
        <Button onClick={() => navigate('/dashboard')} sx={{ mt: 2 }}>Вернуться на главную</Button>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 64px)',
        fontWeight: 300,
        '& .MuiTypography-root': { fontWeight: 300 },
        '& .MuiTypography-h4': { fontWeight: 300, letterSpacing: '-0.035em' },
        '& .MuiTypography-h5': { fontWeight: 300, letterSpacing: '-0.025em' },
        '& .MuiTypography-h6': { fontWeight: 300 },
        '& .MuiTypography-body1, & .MuiTypography-body2, & .MuiTypography-caption': { fontWeight: 300 },
        '& .MuiButton-root': { fontWeight: 300, letterSpacing: '0.015em' },
        pt: { xs: 2, sm: 3 },
        pb: 0,
        px: { xs: 1, sm: 2 },
        position: 'relative',
        overflow: 'hidden',
        background: `radial-gradient(circle at 10% 5%, ${alpha(primary, 0.12)} 0%, transparent 34%), radial-gradient(circle at 95% 90%, ${alpha(primary, 0.09)} 0%, transparent 32%)`,
      }}
    >
      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
        <Paper sx={{ ...glassSurface(theme), borderRadius: { xs: 4, sm: 5 }, overflow: 'hidden' }}>
          {submitted ? (
            <Box sx={{ p: { xs: 4, sm: 6, md: 8 }, textAlign: 'center' }}>
              <Box sx={{ width: 76, height: 76, mx: 'auto', mb: 2.5, display: 'grid', placeItems: 'center', borderRadius: '50%', color: primary, bgcolor: alpha(primary, 0.1), border: `1px solid ${alpha(primary, 0.2)}` }}>
                <CheckCircleRounded sx={{ fontSize: 42 }} />
              </Box>
              <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ mb: 1.5, fontWeight: 300, letterSpacing: '-0.035em' }}>Спасибо за ваш отзыв!</Typography>
              <Typography color="text.secondary" sx={{ maxWidth: 520, mx: 'auto', lineHeight: 1.8, mb: 3 }}>Ответы успешно отправлены. Ваше мнение поможет нам сделать FinCollab удобнее и полезнее.</Typography>
              <Button onClick={() => navigate('/dashboard', { replace: true, state: { questionnaireCompleted: true } })} variant="contained" endIcon={<ArrowForwardRounded />} sx={{ minHeight: 48, px: 3, borderRadius: 99, bgcolor: primary, color: theme.palette.getContrastText(primary), textTransform: 'none', fontWeight: 300 }}>Вернуться на главную</Button>
            </Box>
          ) : (
          <Box sx={{ p: { xs: 2.5, sm: 4, md: 5 } }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1.25}>
                <Box sx={{ width: 42, height: 42, display: 'grid', placeItems: 'center', borderRadius: 3, color: primary, bgcolor: alpha(primary, 0.1), border: `1px solid ${alpha(primary, 0.16)}` }}>
                  <DescriptionOutlined />
                </Box>
                <Box>
                  <Typography fontWeight={300} sx={{ lineHeight: 1.15 }}>FinCollab</Typography>
                  <Typography variant="caption" color="text.secondary">Анкета обратной связи</Typography>
                </Box>
              </Stack>
              <Button
                aria-label="Закрыть анкету"
                onClick={() => navigate('/dashboard')}
                sx={{ minWidth: 40, width: 40, height: 40, borderRadius: '50%', color: 'text.secondary' }}
              >
                <CloseRounded />
              </Button>
            </Stack>

            <Box sx={{ mb: 3 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Вопрос {activeStep + 1} из {questions.length}</Typography>
                <Typography variant="body2" fontWeight={300} color={primary}>{Math.round(progress)}%</Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{ height: 7, borderRadius: 99, bgcolor: alpha(primary, 0.12), '& .MuiLinearProgress-bar': { borderRadius: 99, bgcolor: primary } }}
              />
            </Box>

            {activeStep === 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={300} letterSpacing="-0.035em" sx={{ mb: 1.25 }}>
                  {questionnaire.title || 'Ваше мнение важно для нас'}
                </Typography>
                {questionnaire.description && (
                  <Typography color="text.secondary" sx={{ lineHeight: 1.75, maxWidth: 680 }}>
                    {questionnaire.description}
                  </Typography>
                )}
              </Box>
            )}

            <Box sx={{ pt: { xs: 1, sm: 2 } }}>
              <Typography variant="h6" fontWeight={300} sx={{ lineHeight: 1.4, mb: 2 }}>
                {currentQuestion.text}
                {currentQuestion.is_required && <Box component="span" sx={{ color: theme.palette.error.main }}> *</Box>}
              </Typography>
              {renderQuestion(currentQuestion)}
            </Box>

            {isLastStep && (
              <Paper elevation={0} sx={{ mt: 4, p: { xs: 2, sm: 2.5 }, borderRadius: 3.5, bgcolor: alpha(primary, 0.055), border: `1px solid ${alpha(primary, 0.14)}` }}>
                <FormControlLabel
                  control={<Switch checked={consent} onChange={(event) => { setConsent(event.target.checked); setValidationMessage(''); }} color="primary" />}
                  label={(
                    <Box>
                      <Typography fontWeight={300} variant="body2">Согласен(на) на обработку моих ответов</Typography>
                      <Typography variant="caption" color="text.secondary">Ответы будут использованы для улучшения платформы FinCollab.</Typography>
                    </Box>
                  )}
                  sx={{ alignItems: 'flex-start', m: 0, '& .MuiSwitch-root': { mt: -0.5, mr: 1 } }}
                />
              </Paper>
            )}

            {validationMessage && <Alert severity="warning" sx={{ mt: 2, borderRadius: 3 }}>{validationMessage}</Alert>}
            {submitError && <Alert severity="error" sx={{ mt: 2, borderRadius: 3 }}>{submitError}</Alert>}

            <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1.5} justifyContent="space-between" sx={{ mt: 4 }}>
              <Button
                onClick={activeStep === 0 ? () => navigate('/dashboard') : handleBack}
                startIcon={<ArrowBackRounded />}
                variant="outlined"
                sx={{ minHeight: 48, px: 2.5, borderRadius: 99, borderColor: alpha(primary, 0.45), color: primary, textTransform: 'none', fontWeight: 700, '&:hover': { borderColor: primary, bgcolor: alpha(primary, 0.05) } }}
              >
                {activeStep === 0 ? 'Выйти' : 'Назад'}
              </Button>
              {isLastStep ? (
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  endIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <CheckCircleRounded />}
                  variant="contained"
                  sx={{ minHeight: 48, px: 3, borderRadius: 99, bgcolor: primary, color: theme.palette.getContrastText(primary), textTransform: 'none', fontWeight: 750, boxShadow: `0 8px 22px ${alpha(primary, 0.25)}`, '&:hover': { bgcolor: theme.palette.primary.dark, boxShadow: `0 10px 28px ${alpha(primary, 0.3)}` } }}
                >
                  {submitting ? 'Отправляем…' : 'Отправить ответы'}
                </Button>
              ) : (
                <Button
                  onClick={handleNext}
                  endIcon={<ArrowForwardRounded />}
                  variant="contained"
                  sx={{ minHeight: 48, px: 3, borderRadius: 99, bgcolor: primary, color: theme.palette.getContrastText(primary), textTransform: 'none', fontWeight: 750, boxShadow: `0 8px 22px ${alpha(primary, 0.25)}`, '&:hover': { bgcolor: theme.palette.primary.dark, boxShadow: `0 10px 28px ${alpha(primary, 0.3)}` } }}
                >
                  Далее
                </Button>
              )}
            </Stack>
          </Box>
          )}
        </Paper>
        {!submitted && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 2.5, px: 2 }}>
          Спасибо, что помогаете делать FinCollab лучше.
        </Typography>}
      </Container>
    </Box>
  );
};

export default QuestionnairePage;
